import React, { useContext, useEffect, useState } from 'react'
import API from '../../../services/API'
import { useNavigate, useParams } from 'react-router-dom'
import { GlobalContext, UserContext } from '../../../App'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Modal,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { DetailRow } from '../../../components/Cards/DetailsBox'
import moment from 'moment'
import CircleIcon from '@mui/icons-material/Circle'
import SmallModal from '../../../components/Modals/SmallModal'
import BigModal from '../../../components/Modals/BigModal'
import AddNewPGD from './AddNewPGD'
import {
  CalendarMonthRounded,
  HistoryEduRounded,
  MedicationRounded,
  UpdateRounded,
} from '@mui/icons-material'
import Loading from '../../loading/loadingPage'
import { useDocumentTitle } from '../../../helpers/DocumentTitleManager'

const InfoTile = ({ icon, label, value, multiline }) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: 3,
      bgcolor: 'rgba(255,255,255,0.75)',
      border: '1px solid rgba(15,23,42,0.05)',
      boxShadow: '0 6px 18px rgba(15,23,42,0.035)',
    }}
  >
    <Stack direction="row" spacing={1.2} alignItems="flex-start">
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(35,154,164,0.10)',
          color: '#239AA4',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      <Box>
        <Typography
          variant="caption"
          sx={{
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'rgba(15,23,42,0.45)',
            fontWeight: 700,
          }}
        >
          {label}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            mt: 0.3,
            fontWeight: 600,
            color: 'text.primary',
            whiteSpace: multiline ? 'pre-wrap' : 'normal',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Stack>
  </Box>
)

const PGDPage = () => {
  const [pgdInfo, setPGDInfo] = useState({})
  const { pgdID } = useParams()
  const {setPageTitle} = useDocumentTitle()
  const [fetchError, setFetchError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [docURL, setDocURL] = useState(null)
  const { user, setUser } = useContext(UserContext)
  const [openSignature, setOpenSignature] = useState(false)
  const [confirmedPassword, setConfirmedPassword] = useState('')
  const { setToast } = useContext(GlobalContext)
  const [passwordAttempts, setPasswordAttempts] = useState(0)
  const [wrongPassword, setWrongPassword] = useState(false)
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false)
  const [openEdit, setOpenEdit] = useState(false)
  const [openNewVersion, setOpenNewVersion] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(true)
  const [pdfObjectLoaded, setPdfObjectLoaded] = useState(false)
  const [pdfError, setPdfError] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (pgdInfo?.title) {
      setPageTitle(pgdInfo.title)
    }
  }, [pgdInfo?.title, setPageTitle])

  useEffect(() => {
    if (!pgdInfo?.docLink || isMobile) {
      setPdfLoading(false)
      return
    }

    setPdfLoading(true)
    setPdfObjectLoaded(false)
    setPdfError(false)

    API.post('/gets3downloadurl', {
      key: pgdInfo.docLink,
      ACL: 'public-read',
    })
      .then((res) => {
        if (res.status === 200 && res.data) {
          setDocURL(res.data)
        } else {
          setPdfError(true)
        }
      })
      .catch(() => setPdfError(true))
  }, [pgdInfo?.docLink, isMobile])

  // TIMER FALLBACK IN CASE BROWSER DOESN'T SHOW PDF
  useEffect(() => {
    if (!docURL || isMobile) return

    const timer = setTimeout(() => {
      setPdfLoading(false)
    }, 1500)

    return () => clearTimeout(timer)
  }, [docURL, isMobile])

  const canSign =
    user?.employee?.registrationNumber?.body &&
    user?.employee?.registrationNumber?.number

  const allowedTeams = ['Manager', 'Governance', 'Personnel', 'Supervisor']
  const isGovernanceUser = user.employee?.teams.some((team) =>
    allowedTeams.includes(team),
  )

  useEffect(() => {
    // Detect mobile devices
    const mobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
    setIsMobile(mobile)
  }, [])

  const handleOpenPDF = async () => {
    if (!pgdInfo?.docLink) return

    try {
      const res = await API.post('/gets3downloadurl', {
        key: pgdInfo.docLink,
        ACL: 'public-read',
      })
      if (res.status === 200 && res.data) {
        const url = res.data
        window.open(url, '_blank', 'noopener,noreferrer')
      } else {
        setToast({
          message: 'Unable to fetch document. Please try again later.',
          severity: 'error',
        })
      }
    } catch (err) {
      setToast({
        message: 'Error fetching document link.',
        severity: 'error',
      })
    }
  }

  const handleDelete = () => {
    API.delete('/governance/delete-pgd/' + pgdInfo._id).then((res) => {
      if (res.status === 200) {
        navigate('/pgd-library')
        setToast({
          message: 'PGD Deleted',
        })
      } else {
        setToast({
          message: 'Unable to delete PGD. Please try again later.',
          severity: 'error',
        })
      }
    })
  }

  // Archiving retires a PGD without losing its signatures: it leaves the
  // library and stops counting towards the PGD KPIs.
  const setArchived = (archived) => {
    API.put('/governance/pgd', {
      _id: pgdInfo._id,
      archived,
      ...(archived ? {} : { supersededBy: null }),
    }).then((res) => {
      if (res.status === 200) {
        setToast({ message: archived ? 'PGD Archived' : 'PGD Restored' })
        loadPGD()
      } else {
        setToast({
          message: 'Unable to update PGD. Please try again later.',
          severity: 'error',
        })
      }
    })
  }

  const isPastReview =
    !pgdInfo?.archived &&
    pgdInfo?.reviewDate &&
    moment(pgdInfo.reviewDate).isBefore(moment())

  const signPGDAction = () => {
    API.post(`/governance/sign-pgd/${pgdInfo._id}`, {
      password: confirmedPassword,
    }).then((res) => {
      if (res.status === 200) {
        setConfirmedPassword('')
        setOpenSignature(false)
        loadPGD()
        setToast({ message: 'PGD Signed' })
      } else if (res.status === 400 && res.data === 'Wrong Password') {
        if (passwordAttempts === 2) {
          API.delete('/logout').then(() => setUser({}))
        } else {
          setWrongPassword(true)
          setPasswordAttempts((prev) => prev + 1)
        }
      }
    })
  }

  const loadPGD = () => {
    setLoading(true)
    API.get(`/governance/pgd/${pgdID}`)
      .then((res) => {
        if (res.status === 200) {
          setPGDInfo(res.data)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadPGD()
  }, [])

  return (
    <Box>
      {fetchError ? (
        <Alert severity="error">
          Unable to fetch PGD. Please try again later
        </Alert>
      ) : (
        <Stack height="100%" gap={2}>
          {loading && <LinearProgress />}
          <Grid container spacing={2} height="100%">
            <Grid item xs={12} lg={4} order={{ xs: 1, lg: 2 }}>
              <Stack gap={1}>
                {pgdInfo?.archived && (
                  <Alert severity="info">
                    This PGD is archived and is no longer in use.
                    {pgdInfo.supersededBy?._id && (
                      <>
                        {' '}
                        It was replaced by{' '}
                        <Link
                          href={'/pgd-library/' + pgdInfo.supersededBy._id}
                        >
                          {pgdInfo.supersededBy.title}
                          {pgdInfo.supersededBy.version
                            ? ` (version ${pgdInfo.supersededBy.version})`
                            : ''}
                        </Link>
                        .
                      </>
                    )}
                  </Alert>
                )}
                {isPastReview && (
                  <Alert severity="warning">
                    This PGD passed its review date on{' '}
                    {moment(pgdInfo.reviewDate).format('Do MMM YYYY')}.
                    {isGovernanceUser &&
                      ' Staff can no longer see or sign it. Upload the reviewed version, or archive it if it has been replaced or withdrawn.'}
                  </Alert>
                )}
                {Object.keys(pgdInfo || {}).length > 0 && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: 4,
                      border: '1px solid rgba(15,23,42,0.07)',
                      boxShadow: `
        0 1px 2px rgba(15,23,42,0.03),
        0 10px 28px rgba(15,23,42,0.06)
      `,
                      bgcolor: '#fff',
                    }}
                  >
                    <Stack spacing={2.25}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        spacing={2}
                      >
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              width: 46,
                              height: 46,
                              borderRadius: 3,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'rgba(35,154,164,0.12)',
                              color: '#239AA4',
                            }}
                          >
                            <MedicationRounded />
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                color: 'text.secondary',
                                fontWeight: 800,
                              }}
                            >
                              Patient Group Direction
                            </Typography>

                            <Typography variant="h6" fontWeight={800}>
                              {pgdInfo?.title || 'Untitled PGD'}
                            </Typography>
                          </Box>
                        </Stack>

                        <Chip
                          label={`Version ${pgdInfo?.version || '—'}`}
                          color="primary"
                          variant="outlined"
                          sx={{
                            alignSelf: { xs: 'flex-start', sm: 'center' },
                            borderRadius: 2,
                            fontWeight: 800,
                          }}
                        />
                      </Stack>

                      <Divider />
                      <Box>
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} md={6}>
                            <InfoTile
                              icon={<CalendarMonthRounded />}
                              label="Effective date"
                              value={
                                pgdInfo?.date
                                  ? moment(pgdInfo.date).format('Do MMM YYYY')
                                  : 'Not recorded'
                              }
                            />
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <InfoTile
                              icon={<UpdateRounded />}
                              label="Review date"
                              value={
                                pgdInfo?.reviewDate
                                  ? moment(pgdInfo.reviewDate).format(
                                      'Do MMM YYYY',
                                    )
                                  : 'Not recorded'
                              }
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <InfoTile
                              icon={<HistoryEduRounded />}
                              label="Version changes"
                              value={
                                pgdInfo?.versionChanges ||
                                'No version changes recorded.'
                              }
                              multiline
                            />
                          </Grid>
                          {isGovernanceUser && !isMobile && (
                            <>
                              <Grid item xs={12} lg={6}>
                                <>
                                  <Button
                                    onClick={() => setOpenEdit(true)}
                                    size="small"
                                    color="anMainBlue"
                                    variant="contained"
                                    fullWidth
                                  >
                                    Edit
                                  </Button>
                                  <BigModal
                                    open={openEdit}
                                    onClose={() => setOpenEdit(false)}
                                  >
                                    <AddNewPGD
                                      existingPGD={pgdInfo}
                                      onCloseSubmit={() => {
                                        setOpenEdit(false)
                                        loadPGD()
                                      }}
                                    />
                                  </BigModal>
                                </>
                              </Grid>
                              {!pgdInfo.archived && (
                                <Grid item xs={12} lg={6}>
                                  <Tooltip
                                    describeChild
                                    title="Upload the reviewed PGD as a new version. Staff sign the new version and this one is archived."
                                  >
                                    <Button
                                      onClick={() => setOpenNewVersion(true)}
                                      size="small"
                                      color="anMainBlue"
                                      variant="outlined"
                                      fullWidth
                                    >
                                      New version
                                    </Button>
                                  </Tooltip>
                                  <BigModal
                                    open={openNewVersion}
                                    onClose={() => setOpenNewVersion(false)}
                                  >
                                    <AddNewPGD
                                      replaces={pgdInfo}
                                      onCloseSubmit={() => {
                                        setOpenNewVersion(false)
                                        navigate('/pgd-library')
                                      }}
                                    />
                                  </BigModal>
                                </Grid>
                              )}
                              <Grid item xs={12} lg={6}>
                                <Button
                                  onClick={() => setArchived(!pgdInfo.archived)}
                                  size="small"
                                  color="anMainBlue"
                                  variant="outlined"
                                  fullWidth
                                >
                                  {pgdInfo.archived ? 'Restore' : 'Archive'}
                                </Button>
                              </Grid>
                              <Grid item xs={12} lg={6}>
                                <Button
                                  onClick={() => setOpenConfirmDelete(true)}
                                  size="small"
                                  color="anRed"
                                  variant="contained"
                                  fullWidth
                                >
                                  Delete
                                </Button>
                                <SmallModal
                                  open={openConfirmDelete}
                                  onClose={() => setOpenConfirmDelete(false)}
                                >
                                  <Stack gap={0.5}>
                                    <Typography textAlign="center">
                                      Are you sure you want to delete this PGD?
                                    </Typography>
                                    <Button
                                      color="error"
                                      onClick={handleDelete}
                                      variant="contained"
                                    >
                                      Yes - Delete
                                    </Button>
                                    <Button
                                      color="primary"
                                      onClick={() =>
                                        setOpenConfirmDelete(false)
                                      }
                                      variant="contained"
                                    >
                                      No - Cancel
                                    </Button>
                                  </Stack>
                                </SmallModal>
                              </Grid>
                            </>
                          )}
                          <Grid item xs={12}>
                            <Stack
                              direction={{ xs: 'column', lg: 'row' }}
                              justifyContent="space-between"
                            >
                              {isGovernanceUser && !isMobile && (
                                <Stack direction="row" gap={1}></Stack>
                              )}
                            </Stack>
                          </Grid>
                        </Grid>
                      </Box>
                    </Stack>
                  </Paper>
                )}
                <Stack component={Paper} padding={2}>
                  {!canSign ? (
                    <Alert severity="info">
                      You are not eligible to sign this PGD.
                    </Alert>
                  ) : !pgdInfo?.signedByMe ? (
                    <>
                      <Button
                        onClick={() => setOpenSignature(true)}
                        variant="contained"
                        fullWidth
                        color="anPurple"
                      >
                        Sign this PGD
                      </Button>
                      <BigModal
                        style={{ width: '20rem', maxWidth: '90rem' }}
                        open={openSignature}
                        onClose={() => setOpenSignature(false)}
                      >
                        <Stack gap={1} alignItems="center">
                          <Typography variant="title3">
                            Sign this PGD
                          </Typography>
                          <Typography variant="caption">
                            By entering my password below I confirm that I have
                            read, understood, and agree to adhere to the above
                            Patient Group Direction. I acknowledge that I am
                            professionally responsible for ensuring I am
                            competent to operate under this PGD.
                          </Typography>
                          <TextField
                            fullWidth
                            label="Password"
                            type="password"
                            value={confirmedPassword}
                            error={wrongPassword}
                            onChange={(e) =>
                              setConfirmedPassword(e.target.value)
                            }
                            helperText={
                              passwordAttempts > 0 &&
                              `Remaining Attempts: ${3 - passwordAttempts}`
                            }
                          />
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => setOpenSignature(false)}
                            color="error"
                          >
                            Close
                          </Button>
                          <Button
                            fullWidth
                            disabled={!confirmedPassword}
                            variant="contained"
                            color="success"
                            onClick={() => signPGDAction()}
                          >
                            Submit
                          </Button>
                        </Stack>
                      </BigModal>
                    </>
                  ) : (
                    <Chip
                      color="success"
                      label="PGD Signed"
                      sx={{ borderRadius: 1 }}
                    />
                  )}
                </Stack>
                {pgdInfo.shouldBeSignedBy && (
                  <Stack component={Paper} padding={2} gap={1}>
                    <Typography variant="subtitle1">Signatures</Typography>
                    <List disablePadding>
                      {pgdInfo.shouldBeSignedBy.map((employee) => {
                        const signature = pgdInfo.signedBy.find(
                          (x) => x.user._id === employee.userAccount._id,
                        )
                        return (
                          <ListItem
                            key={employee._id}
                            divider
                            secondaryAction={
                              signature ? (
                                <Chip
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  label={`Signed ${moment(signature.signed).format('DD/MM/YYYY')}`}
                                  sx={{ borderRadius: 1 }}
                                />
                              ) : (
                                <Chip
                                  size="small"
                                  color="error"
                                  variant="filled"
                                  label="Not signed"
                                  sx={{ borderRadius: 1 }}
                                />
                              )
                            }
                            sx={{
                              py: 1.25,

                              '&:hover': {
                                bgcolor: 'rgba(15,23,42,0.025)',
                              },
                            }}
                          >
                            <ListItemAvatar>
                              <Avatar sx={{ width: 34, height: 34 }}>
                                {employee.userAccount.firstName?.[0]}

                                {employee.userAccount.lastName?.[0]}
                              </Avatar>
                            </ListItemAvatar>

                            <ListItemText
                              primary={
                                <Typography fontWeight={700}>
                                  {employee.userAccount.firstName}{' '}
                                  {employee.userAccount.lastName}
                                </Typography>
                              }
                              secondary={
                                employee.clinicalRole ||
                                'No clinical role recorded'
                              }
                            />
                          </ListItem>
                        )
                      })}
                    </List>

                    <Typography variant="caption">
                      AmbuNet will only show Employees who are registered HCPs
                      and have the Clinical Role assigned to this PGD.
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Grid>
            <Grid item xs={12} lg={8} order={{ xs: 2, lg: 1 }}>
              <Box
                height="100%"
                display="flex"
                position="relative"
                alignItems="center"
                justifyContent="center"
              >
                {isMobile ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOpenPDF}
                    fullWidth
                  >
                    Open PGD Document
                  </Button>
                ) : pdfError ? (
                  <Stack
                    spacing={2}
                    alignItems="center"
                    justifyContent="center"
                    sx={{ width: '100%', height: '100%', minHeight: 300 }}
                  >
                    <Alert severity="warning">
                      Unable to preview this PDF document.
                    </Alert>
                    <Link href={docURL} target="_blank" underline="none">
                      <Button variant="contained">Open Document</Button>
                    </Link>
                  </Stack>
                ) : (
                  <>
                    {pdfLoading && (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          zIndex: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Loading />
                      </Box>
                    )}

                    <object
                      data={docURL}
                      type="application/pdf"
                      width="100%"
                      height="100%"
                      onLoad={() => setPdfLoading(false)}
                      style={{
                        border: 'none',
                        borderRadius: 12,
                        opacity: pdfLoading ? 0 : 1,
                      }}
                    />
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </Stack>
      )}
    </Box>
  )
}

export default PGDPage
